import { AfterViewInit, Component, effect, inject, ViewChild, WritableSignal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import type { IError } from '@suvo-bi-core';
import type { Refreshable } from '@suvo/frontend/standalone/signals/refreshable.interface';
import { FilterConditionType } from '@suvo/frontend/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/shared/interfaces/filters/filter-condition-type.enum';
import { FilterOperatorType } from '@suvo/frontend/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/shared/interfaces/filters/filter-operator-type.enum';
import { EditComponent } from '../../../../../shared/components/edit/edit.component';
import { STATUSES, type TStatus } from '../../../../../shared/components/status-chip/status.type';
import { IRecordingEvent } from '../../interface/recording-event.interface';
import type { ITow } from '../../interface/tow.interface';
import { TowsService } from '../../service/tows.service';

@Component({
  selector: 'app-tow-edit-screen',
  templateUrl: './tow-edit-screen.component.html',
  styles: `
    .input-group {
      display: flex;
      flex-flow: row wrap;
      gap: var(--global-padding);
      justify-content: left;
      margin: var(--global-padding) 0;

      > * {
        flex-grow: 1;
      }
    }

    :host ::ng-deep .mat-form-field-wrapper {
      padding-bottom: 0 !important;
    }
  `,
})
export class TowEditScreenComponent implements AfterViewInit {
  @ViewChild('editComponent') editComponent: EditComponent;

  shipFilters = {
    name: 'Ship Filters',
    query: {
      op: FilterOperatorType.And,
      rules: [{ key: 'endDate', con: [{ type: FilterConditionType.NotExists }] }],
    },
  };

  readonly formGroup = new FormGroup({
    oldTowBaseId: new FormControl<string>(''),
    status: new FormControl<TStatus>('created'),
    month: new FormControl<number>(null),
    year: new FormControl<number>(null),
    routeId: new FormControl<string>(''),
    shipId: new FormControl<string>(''),
    cprId: new FormControl<string>(''),
    internalId: new FormControl<string>(''),
    silkReadingStart: new FormControl<number>(null),
    silkReadingEnd: new FormControl<number>(null),
    message: new FormControl<string>(''),
    validity: new FormControl<string>(''),
    instrumentId: new FormControl<string>(''),
    serialNumberPerCalendarMonth: new FormControl<number>(null),
    propSetting: new FormControl<number>(null),
    routeStatus: new FormControl<string>(''),
    factor: new FormControl<number>(null),
    masterName: new FormControl<string>(''),
    partOne: new FormControl<number>(null),
    partTwo: new FormControl<number>(null),
    fromPortName: new FormControl<string>(''),
    toPortName: new FormControl<string>(''),
    instrumentDataFile: new FormControl<string>(''),
    cut: new FormControl<string>(undefined, {
      validators: (control) => {
        const value = control.value;

        if (!value) {
          return null;
        }

        if (typeof value === 'string') {
          const split = value.split('/').map((n) => parseInt(n));

          if (split.length == 3 && !split.some((n) => Number.isNaN(n))) {
            const [day, month, year] = split;

            if (day > 0 && day <= 31 && month > 0 && month <= 12 && year >= 1900 && year < 2100) {
              return null;
            }
          }
        }

        return { invalidDate: true };
      },
    }),
  });

  readonly statusOptions = STATUSES.filter((status) => status !== 'finalised');

  readonly validityOptions = ['JAM', 'LOST', 'OK', 'QLA', 'QUAL'] as const;

  protected readonly tow?: WritableSignal<IRecordingEvent<'tow'>> & Refreshable<Promise<void>> =
    inject(ActivatedRoute).snapshot.data.tow;

  serverErrors: IError[] | null = null;

  private readonly onTowChange = effect(
    () => {
      const tow = this.tow?.();

      if (tow) {
        this.formGroup.setValue({
          oldTowBaseId: tow.metadata.oldTowBaseId,
          status: tow.metadata.status ?? 'created',
          routeId: tow.metadata.routeId ?? '',
          instrumentId: tow.metadata.instrumentId ?? '',
          shipId: tow.metadata.shipId ?? '',
          cprId: tow.metadata.cprId ?? '',
          internalId: tow.metadata.internalId ?? '',
          serialNumberPerCalendarMonth: tow.metadata.serialNumberPerCalendarMonth
            ? Number(tow.metadata.serialNumberPerCalendarMonth)
            : null,
          propSetting: tow.metadata.propSetting ?? null,
          routeStatus: tow.metadata.routeStatus ?? '',
          message: tow.metadata.message ?? '',
          factor: tow.metadata.factor ?? null,
          silkReadingStart: tow.metadata.silkReadingStart ?? null,
          silkReadingEnd: tow.metadata.silkReadingEnd ?? null,
          masterName: tow.metadata.masterName ?? '',
          partOne: tow.metadata.partOne ?? null,
          partTwo: tow.metadata.partTwo ?? null,
          fromPortName: tow.metadata.fromPortName ?? '',
          toPortName: tow.metadata.toPortName ?? '',
          instrumentDataFile: tow.metadata.instrumentDataFile ?? '',
          month: tow.metadata.month ?? null,
          year: tow.metadata.year ?? null,
          validity: tow.metadata.validity ?? '',
          cut: tow.metadata.cut
            ? `${(tow.metadata.cut as string).slice(8, 10)}/${(tow.metadata.cut as string).slice(5, 7)}/${(tow.metadata.cut as string).slice(0, 4)}`
            : null,
        });
        this.formGroup.markAsPristine();
        this.editComponent?.markAsUpdated();
      }

      this.syncReadonlyState();
    },
    { allowSignalWrites: true },
  );

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly towsService: TowsService,
  ) {}

  ngOnDestroy() {
    this.onTowChange.destroy();
  }

  ngAfterViewInit() {
    this.syncReadonlyState();
  }

  private syncReadonlyState() {
    const tow = this.tow?.();
    const editComponent = this.editComponent;

    if (!tow || !editComponent) {
      return;
    }

    if (tow.metadata.status === 'finalised') {
      editComponent.formGroup.disable({ emitEvent: false });
    } else if (editComponent.hasPermission && !editComponent.useEditButton) {
      editComponent.formGroup.enable({ emitEvent: false });
    }
  }

  async onSave($event: ITow) {
    for (const property of ['cprId', 'routeId', 'shipId']) {
      if ($event[property] instanceof Array) {
        $event[property] = $event[property][0];
      }
    }

    if ($event.cut) {
      const split = ($event.cut as string).split('/').map((n) => Number(n));
      if (split.some((n) => Number.isNaN(n))) {
        this.serverErrors = [{ message: 'Invalid cut date' }];
      }
      $event.cut = new Date(Date.UTC(split[2], split[1] - 1, split[0])).toISOString().slice(0, 10);
    } else {
      $event.cut = null;
    }

    this.serverErrors = null;

    try {
      if (this.tow?.()?._id) {
        await this.towsService.replaceMetadata(this.tow()._id, $event);
        this.editComponent.markAsUpdated();
        this.tow.refresh();
      } else {
        const { _id } = await this.towsService.postOne('', {
          type: 'tow',
          metadata: $event,
        });
        await this.router.navigate(['..', _id], { relativeTo: this.route });
      }
    } catch (e) {
      this.serverErrors = [e.error];
      this.editComponent.enable();
    }
  }
}
